// ignore-string-externalization

import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { useLocale } from '../../../features/src/i18n';
import { getContentfulLocale } from '../../../features/src/i18n/locales';
import { CategoryObject } from './types';
import type { BlogItem } from './blogQueries';
import { fetchBlogData } from './blogFetchers';
import { normalizeBlogItems } from './blogTransformers';

export { fetchBlogData, fetchHeros } from './blogFetchers';

type CategoryMeta = Record<string, { total?: number; offset: number }>;

type InitialBlogData = {
  items: BlogItem[];
  total: number;
};

type HeroData = {
  heros: Array<{
    listeners: any;
    artistId: string | null | undefined;
    artistName: string | null | undefined;
    bannerImage: string | null | undefined;
  }>;
  heroIndex: number;
};

type BlogContextValue = {
  hero: HeroData['heros'][number] | undefined;
  blogPosts: ReturnType<typeof normalizeBlogItems>['blogPosts'] | null;
  blogCategories:
    | ReturnType<typeof normalizeBlogItems>['blogCategories']
    | null;
  loadMore: (category: CategoryObject) => Promise<void>;
  loadingMore: boolean;
};

const BlogDataContext = createContext<BlogContextValue>({
  hero: undefined,
  blogPosts: null,
  blogCategories: null,
  loadMore: async () => {},
  loadingMore: false,
});

export const BlogDataProvider = ({
  initialData,
  heroData,
  children,
}: {
  initialData: InitialBlogData | null;
  heroData: HeroData | null;
  children: React.ReactNode;
}) => {
  const locale = useLocale();
  const smartlingLanguage = getContentfulLocale(locale);
  const [loadingMore, setLoadingMore] = useState(false);
  const [extraItems, setExtraItems] = useState<BlogItem[]>([]);
  const [categoryMeta, setCategoryMeta] = useState<CategoryMeta>({});

  const blogState = useMemo(() => {
    if (!initialData) return null;

    const allItems = [...initialData.items, ...extraItems];
    const seen = new Set<string>();
    const deduped = allItems.filter(item => {
      if (seen.has(item.sys.id)) return false;
      seen.add(item.sys.id);
      return true;
    });

    const state = normalizeBlogItems(deduped);

    state.blogCategories.bySlug.all.total =
      categoryMeta.all?.total ?? initialData.total;
    state.blogCategories.bySlug.all.offset = categoryMeta.all?.offset ?? 0;

    for (const [slug, meta] of Object.entries(categoryMeta)) {
      if (slug !== 'all' && state.blogCategories.bySlug[slug]) {
        state.blogCategories.bySlug[slug].total = meta.total;
        state.blogCategories.bySlug[slug].offset = meta.offset;
      }
    }

    return state;
  }, [initialData, extraItems, categoryMeta]);

  const loadMore = useCallback(
    async (category: CategoryObject) => {
      setLoadingMore(true);
      try {
        const result = await fetchBlogData({
          smartlingLanguage,
          offset: category.offset + 10,
        });
        setExtraItems(prev => [...prev, ...result.items]);
        setCategoryMeta(prev => ({
          ...prev,
          [category.urlSlug]: {
            total: result.total,
            offset: category.offset + 10,
          },
        }));
      } finally {
        setLoadingMore(false);
      }
    },
    [smartlingLanguage],
  );

  const hero = heroData ? heroData.heros[heroData.heroIndex] : undefined;

  return (
    <BlogDataContext.Provider
      value={{
        hero,
        blogPosts: blogState?.blogPosts ?? null,
        blogCategories: blogState?.blogCategories ?? null,
        loadMore,
        loadingMore,
      }}
    >
      {children}
    </BlogDataContext.Provider>
  );
};

export const useBlogData = () => useContext(BlogDataContext);
